/**
 * RemoveDriverDialog Component
 *
 * Confirmation dialog for removing a driver column
 *
 * Features:
 * - Warning about assigned jobs
 * - Confirm/cancel actions
 * - Accessible alert dialog
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

interface RemoveDriverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driverName: string;
  assignmentCount: number;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function RemoveDriverDialog({
  open,
  onOpenChange,
  driverName,
  assignmentCount,
  onConfirm,
  isLoading = false,
}: RemoveDriverDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            Remove Driver
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to remove <strong>{driverName}</strong> from the dispatch board?
          </DialogDescription>
        </DialogHeader>

        {assignmentCount > 0 && (
          <div className="py-4 px-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This driver has {assignmentCount} assigned job
              {assignmentCount !== 1 ? 's' : ''}. You must unassign all jobs before removing this driver.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading || assignmentCount > 0}
          >
            {isLoading ? 'Removing...' : 'Remove Driver'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
